import { useForm, useWatch, type Control, type FieldPath } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { FormSection, MoneyInput, TagInput } from '@nucleus-commerce/admin-kit/forms';
import { useSaveBar } from '@nucleus-commerce/admin-kit/save-bar';
import {
  PolicySettingsReturnsRefundType,
  PolicySettingsWarrantyKind,
  type Store,
} from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { Switch } from '@nucleus-commerce/ui/components/switch';

import { useUpdateStore } from '@/features/stores';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';
import { SECTION_ANCHORS } from '@/shared/navigation/section-anchors';
import { useStoreEditable } from '@/shared/store/use-store-editable';

import { policyHighlights } from '../schemas/policy-highlights';
import {
  EXCLUSION_LENGTH_MAX,
  EXCLUSIONS_MAX,
  fromPolicySettings,
  POLICY_SETTINGS_FIELDS,
  policySettingsField,
  policySettingsSchema,
  toPolicySettings,
  toPolicySettingsPatch,
  type PolicySettingsForm,
} from '../schemas/policy-settings-schema';

type ExclusionsPath = Extract<
  FieldPath<PolicySettingsForm>,
  'returns.exclusions' | 'warranty.exclusions' | 'resizing.exclusions'
>;

function ExclusionsField({
  control,
  name,
  label,
  hint,
  disabled,
}: {
  control: Control<PolicySettingsForm>;
  name: ExclusionsPath;
  label: string;
  hint: string;
  disabled: boolean;
}) {
  const { t } = useTranslation('policies');
  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <TagInput
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              disabled={disabled}
              max={EXCLUSIONS_MAX}
              maxLength={EXCLUSION_LENGTH_MAX}
              placeholder={t('settings.exclusionsPlaceholder')}
              aria-invalid={fieldState.invalid}
              removeLabel={(tag) => t('settings.removeExclusion', { value: tag })}
            />
          </FormControl>
          <FormDescription>{hint}</FormDescription>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

/** What the storefront will render, recomputed from the unsaved form. */
function HighlightsPreview({ store }: { store: Store }) {
  const { t } = useTranslation('policies');
  const values = useWatch<PolicySettingsForm>();
  const parsed = policySettingsSchema.safeParse(values);
  const bullets = parsed.success
    ? policyHighlights(toPolicySettings(parsed.data), store.settings.returns, store.defaultCurrency)
    : null;
  return (
    <div className="bg-muted/40 rounded-md border p-4" data-testid="policy-highlights">
      <p className="text-sm font-medium">{t('settings.preview.title')}</p>
      <p className="text-muted-foreground mb-3 text-xs">{t('settings.preview.description')}</p>
      {bullets === null ? (
        <p className="text-muted-foreground text-sm">{t('settings.preview.invalid')}</p>
      ) : bullets.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('settings.preview.empty')}</p>
      ) : (
        <ul className="flex flex-wrap gap-1.5" aria-label={t('settings.preview.title')}>
          {bullets.map((bullet) => (
            <li
              key={bullet}
              className="bg-background rounded-full border px-2.5 py-0.5 text-xs font-medium"
            >
              {bullet}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * `settings.policies` of the store: the structured facts behind the shipping, returns, warranty
 * and resizing policies. They become the highlight bullets on product pages and the footer and
 * feed schema.org MerchantReturnPolicy and OfferShippingDetails; the documents hold the prose.
 */
export function PolicySettingsCard({ store }: { store: Store }) {
  const { t } = useTranslation(['policies', 'common']);
  const readOnly = !useStoreEditable({ store, permission: 'settings.write' }).editable;
  const update = useUpdateStore(store.id, 'policies:toasts.settingsSaved');
  const message = useErrorMessage();
  const form = useForm<PolicySettingsForm>({
    resolver: useI18nResolver(policySettingsSchema),
    defaultValues: fromPolicySettings(store),
  });
  const freeShipping = form.watch('shipping.freeShipping');
  const processingDays = form.watch('shipping.processingDays');
  const warrantyKind = form.watch('warranty.kind');
  const freeResizing = form.watch('resizing.freeResizing');

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    try {
      await update.mutateAsync(toPolicySettingsPatch(values));
      form.reset(values);
    } catch (error) {
      if (
        applyFieldErrors(error, form.setError, {
          knownFields: POLICY_SETTINGS_FIELDS,
          mapField: policySettingsField,
        })
      ) {
        return;
      }
      form.setError('root.server', { type: 'server', message: message(error) });
    }
  });

  useSaveBar({ form, onSave: submit, saving: update.isPending, disabled: readOnly });

  return (
    <FormSection
      id={SECTION_ANCHORS.policyFacts}
      tour="settings.policies.facts"
      title={t('policies:settings.title')}
      description={t('policies:settings.description')}
    >
      <Form {...form}>
        <form onSubmit={submit} noValidate className="flex flex-col gap-6">
          {form.formState.errors.root?.server?.message ? (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
            </Alert>
          ) : null}

          <HighlightsPreview store={store} />

          <fieldset className="flex flex-col gap-4">
            <legend className="mb-2 text-sm font-semibold">
              {t('policies:settings.shipping.title')}
            </legend>
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="shipping.freeShipping"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('policies:settings.shipping.freeShipping')}</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange} disabled={readOnly}>
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {(['none', 'always', 'over'] as const).map((option) => (
                          <SelectItem key={option} value={option}>
                            {t(`policies:settings.shipping.freeShippingOption.${option}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {freeShipping === 'over' ? (
                <FormField
                  control={form.control}
                  name="shipping.freeOver"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('policies:settings.shipping.freeOver')}</FormLabel>
                      <FormControl>
                        <MoneyInput
                          currency={store.defaultCurrency}
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          disabled={readOnly}
                        />
                      </FormControl>
                      <FormDescription>
                        {t('policies:settings.shipping.freeOverHint', {
                          currency: store.defaultCurrency,
                        })}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ) : null}
            </div>
            <FormField
              control={form.control}
              name="shipping.processingDays"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between gap-4 rounded-md border p-3">
                  <div className="grid gap-1">
                    <FormLabel>{t('policies:settings.shipping.processingDays')}</FormLabel>
                    <FormDescription>
                      {t('policies:settings.shipping.processingDaysHint')}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={readOnly}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            {processingDays ? (
              <div className="grid gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="shipping.processingMin"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('policies:settings.shipping.processingMin')}</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          inputMode="numeric"
                          min={0}
                          max={365}
                          disabled={readOnly}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="shipping.processingMax"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('policies:settings.shipping.processingMax')}</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          inputMode="numeric"
                          min={0}
                          max={365}
                          disabled={readOnly}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            ) : null}
            <FormField
              control={form.control}
              name="shipping.regionsNote"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('policies:settings.shipping.regionsNote')}</FormLabel>
                  <FormControl>
                    <Input maxLength={300} disabled={readOnly} {...field} />
                  </FormControl>
                  <FormDescription>
                    {t('policies:settings.shipping.regionsNoteHint')}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </fieldset>

          <fieldset className="flex flex-col gap-4 border-t pt-6">
            <legend className="sr-only">{t('policies:settings.returns.title')}</legend>
            <p className="text-sm font-semibold">{t('policies:settings.returns.title')}</p>
            <p className="text-muted-foreground -mt-3 text-xs">
              {t('policies:settings.returns.description')}
            </p>
            <FormField
              control={form.control}
              name="returns.refundType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('policies:settings.returns.refundType')}</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange} disabled={readOnly}>
                    <FormControl>
                      <SelectTrigger className="w-full sm:w-72">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {Object.values(PolicySettingsReturnsRefundType).map((option) => (
                        <SelectItem key={option} value={option}>
                          {t(`policies:settings.returns.refundTypeOption.${option}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <ExclusionsField
              control={form.control}
              name="returns.exclusions"
              label={t('policies:settings.returns.exclusions')}
              hint={t('policies:settings.returns.exclusionsHint')}
              disabled={readOnly}
            />
          </fieldset>

          <fieldset className="flex flex-col gap-4 border-t pt-6">
            <legend className="sr-only">{t('policies:settings.warranty.title')}</legend>
            <p className="text-sm font-semibold">{t('policies:settings.warranty.title')}</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="warranty.kind"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('policies:settings.warranty.kind')}</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange} disabled={readOnly}>
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Object.values(PolicySettingsWarrantyKind).map((option) => (
                          <SelectItem key={option} value={option}>
                            {t(`policies:settings.warranty.kindOption.${option}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {warrantyKind === 'limited' ? (
                <FormField
                  control={form.control}
                  name="warranty.years"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('policies:settings.warranty.years')}</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          inputMode="numeric"
                          min={1}
                          max={100}
                          disabled={readOnly}
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>{t('policies:settings.warranty.yearsHint')}</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ) : null}
            </div>
            {warrantyKind !== 'none' ? (
              <ExclusionsField
                control={form.control}
                name="warranty.exclusions"
                label={t('policies:settings.warranty.exclusions')}
                hint={t('policies:settings.warranty.exclusionsHint')}
                disabled={readOnly}
              />
            ) : null}
          </fieldset>

          <fieldset className="flex flex-col gap-4 border-t pt-6">
            <legend className="sr-only">{t('policies:settings.resizing.title')}</legend>
            <p className="text-sm font-semibold">{t('policies:settings.resizing.title')}</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="resizing.freeResizing"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('policies:settings.resizing.freeResizing')}</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange} disabled={readOnly}>
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {(['none', 'always', 'within'] as const).map((option) => (
                          <SelectItem key={option} value={option}>
                            {t(`policies:settings.resizing.freeResizingOption.${option}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {freeResizing === 'within' ? (
                <FormField
                  control={form.control}
                  name="resizing.months"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('policies:settings.resizing.months')}</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          inputMode="numeric"
                          min={1}
                          max={120}
                          disabled={readOnly}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ) : null}
            </div>
            {freeResizing !== 'none' ? (
              <ExclusionsField
                control={form.control}
                name="resizing.exclusions"
                label={t('policies:settings.resizing.exclusions')}
                hint={t('policies:settings.resizing.exclusionsHint')}
                disabled={readOnly}
              />
            ) : null}
          </fieldset>

          {readOnly ? null : (
            <div className="border-t pt-4">
              <Button type="submit" disabled={update.isPending || !form.formState.isDirty}>
                {update.isPending ? t('common:actions.saving') : t('policies:settings.submit')}
              </Button>
            </div>
          )}
        </form>
      </Form>
    </FormSection>
  );
}
